/*
 * vv-design-config.js — Design Online classification layer.
 *
 * This is NOT a catalogue. Sizes, prices, availability and variant IDs come from
 * LIVE Shopify data (product.variants / /products/<handle>.js). This file holds only
 * the per-product CLASSIFICATION that Shopify data alone can't give:
 *   - flow: DIRECT | MODAL | PRODUCT_PAGE
 *   - designFields: which option NAMES are design-affecting (customer must choose)
 *   - face: how physical faces map to artwork surfaces (from the real compositor audit)
 *   - customSizeValue: the option value that routes to the product-page configurator (hybrid)
 *
 * Seeded from the approved 45-product classification + fixed-face audit + default audit
 * (all 45 ORDER-SAFE → no default overrides).
 */
(function (root) {
  "use strict";

  // face specs: SINGLE (1), SIDES (1 or 2 from a sides option), FIXED (N faces)
  var F_SINGLE = { kind: "single" };
  function SIDES(opt){ return { kind: "sides", option: opt }; }                 // single→1/SAME? ; double→2/SAME default
  function FIXED(p, s, mode){ return { kind: "fixed", physicalFaceCount: p, artworkSurfaceCount: s, artworkMode: mode }; }

  var CONFIG = {
    // ---- MODAL: single design size field ----
    "pullup-banners":                {flow:"MODAL", designFields:["Size"], face:F_SINGLE},
    "printed-signs-only":            {flow:"MODAL", designFields:["Size"], face:F_SINGLE},
    "loose-table-cloths-throws":     {flow:"MODAL", designFields:["Size"], face:F_SINGLE},
    "stretch-tablecloths-throws":    {flow:"MODAL", designFields:["Size"], face:F_SINGLE},
    "table-runner":                  {flow:"MODAL", designFields:["Size"], face:F_SINGLE},
    "custom-selfie-frames":          {flow:"MODAL", designFields:["Size"], face:F_SINGLE},
    "wedding-signs":                 {flow:"MODAL", designFields:["Display Size"], face:F_SINGLE},
    "novelty-cheque":                {flow:"MODAL", designFields:["Size"], face:F_SINGLE},
    "octanorm-shell-scheme-graphics":{flow:"MODAL", designFields:["Size"], face:F_SINGLE},
    "real-estate-hanging-banners":   {flow:"MODAL", designFields:["Size"], face:F_SINGLE},
    "shell-scheme-graphics":         {flow:"MODAL", designFields:["Size"], face:F_SINGLE},
    "custom-landscape-flags":        {flow:"MODAL", designFields:["Size and Flag Orientation"], face:F_SINGLE},
    "bollard-cover-signs":           {flow:"MODAL", designFields:["Display Shape & Size"], face:FIXED(3,3,"INDEPENDENT_ARTWORK")},
    "corrugated-marketing-cubes":    {flow:"MODAL", designFields:["Cube Size"], face:FIXED(4,4,"INDEPENDENT_ARTWORK")},

    // ---- MODAL: size + multi-geometry (style/backside) ----
    "table-throws":                  {flow:"MODAL", designFields:["Style","Size","Backside"], face:F_SINGLE},
    "fitted-table-cloths-throws":    {flow:"MODAL", designFields:["Size","Backside"], face:F_SINGLE},
    "custom-round-table-throws":     {flow:"MODAL", designFields:["Size","Style"], face:F_SINGLE},

    // ---- MODAL: size + sides choice (single/double) ----
    "media-wall":                    {flow:"MODAL", designFields:["Size","Printed Sides"], face:SIDES("Printed Sides")},
    "straight-stretch-fabric-walls": {flow:"MODAL", designFields:["Size","Printed Sides"], face:SIDES("Printed Sides")},
    "feather-flags":                 {flow:"MODAL", designFields:["Size","Printed Sides"], face:SIDES("Printed Sides")},
    "teardrop-banners-flags":        {flow:"MODAL", designFields:["Size","Printed Sides"], face:SIDES("Printed Sides")},
    "rectangle-banners-flags":       {flow:"MODAL", designFields:["Size","Printed Sides"], face:SIDES("Printed Sides")},
    "bow-head-flags":                {flow:"MODAL", designFields:["Size","Printed Sides"], face:SIDES("Printed Sides")},
    "real-estate-flags":             {flow:"MODAL", designFields:["Size","Printed Sides"], face:SIDES("Printed Sides")},

    // ---- MODAL: size, fixed multi-face (no sides question; faces fixed) ----
    "corrugated-a-frame-signs":      {flow:"MODAL", designFields:["Size"], face:FIXED(2,1,"SAME_ARTWORK")},
    "caution-wet-floor-a-frame-safety-sign":{flow:"MODAL", designFields:["Size"], face:FIXED(2,1,"SAME_ARTWORK")},
    "triangle-roster-signs":         {flow:"MODAL", designFields:["Size"], face:FIXED(2,1,"SAME_ARTWORK")},
    "triangle-safety-signs":         {flow:"MODAL", designFields:["Size"], face:FIXED(2,1,"SAME_ARTWORK")},
    "safety-a-frame-floor-signs":    {flow:"MODAL", designFields:["Display Size"], face:FIXED(2,1,"SAME_ARTWORK")},
    "fabric-banner-stand":           {flow:"MODAL", designFields:["Size"], face:FIXED(2,1,"SAME_ARTWORK")},
    "crowd-control-barriers":        {flow:"MODAL", designFields:["Size"], face:FIXED(2,1,"SAME_ARTWORK")},
    "cafe-barrier":                  {flow:"MODAL", designFields:["Size"], face:FIXED(2,1,"SAME_ARTWORK")},
    "premium-backlit-banner-stand-with-seg-finish":{flow:"MODAL", designFields:["Size"], face:FIXED(2,1,"SAME_ARTWORK")},

    // ---- MODAL hybrid: standard sizes modal, "Custom Size" → product page ----
    "fabric-banners":                {flow:"MODAL", designFields:["Size and Flag Orientation"], face:F_SINGLE, customSizeValue:"Custom Size"},
    "pvc-foam-board-signs":          {flow:"MODAL", designFields:["Display Size"], face:F_SINGLE, customSizeValue:"Custom Size"},

    // ---- DIRECT: one orderable geometry ----
    "premium-pullup-banners":        {flow:"DIRECT", designFields:[], face:F_SINGLE},
    "a-frame-signs":                 {flow:"DIRECT", designFields:[], face:FIXED(2,1,"SAME_ARTWORK")},   // two signs, one design
    "advertising-promotional-hand-waver-signs-flags":{flow:"DIRECT", designFields:[], face:F_SINGLE},   // "Size" = pack qty
    "building-construction-safety-sign":{flow:"DIRECT", designFields:[], face:F_SINGLE},
    "multimedia-displays":           {flow:"DIRECT", designFields:[], face:FIXED(2,1,"SAME_ARTWORK")},
    "plastic-election-signs":        {flow:"DIRECT", designFields:[], face:F_SINGLE},                    // "Size" single; pack qty
    "exhibition-display-stand":      {flow:"DIRECT", designFields:[], face:F_SINGLE},
    "pop-up-led-counter":            {flow:"DIRECT", designFields:[], face:FIXED(2,1,"SAME_ARTWORK")},

    // ---- PRODUCT_PAGE: ITORIS custom size only (no real size variant) ----
    "corrugated-plastic-signs":      {flow:"PRODUCT_PAGE"},
    "plastic-flute-board-signs":     {flow:"PRODUCT_PAGE"},

    // ---- Tier A (approved 2026-10-04): were live-exposed via product-page "own", unclassified ----
    "aluminium-signs":               {flow:"PRODUCT_PAGE"},
    "vinyl-banners":                 {flow:"MODAL", designFields:["Size (mm)"], face:F_SINGLE},
    "replacement-fabric-flags":      {flow:"MODAL", designFields:["Flag Size","Printed Sides"], face:SIDES("Printed Sides")}
  };

  // Resolve the face descriptor for a chosen config (SAME is the default; editor tabs allow INDEPENDENT).
  function faceFor(handle, chosen){
    var c = (CONFIG[handle]||{}).face || F_SINGLE;
    if (c.kind === "fixed") return { physicalFaceCount:c.physicalFaceCount, artworkSurfaceCount:c.artworkSurfaceCount, artworkMode:c.artworkMode };
    if (c.kind === "sides"){
      var dbl = /double/i.test((chosen && chosen[c.option]) || "");
      return dbl ? { physicalFaceCount:2, artworkSurfaceCount:1, artworkMode:"SAME_ARTWORK" }
                 : { physicalFaceCount:1, artworkSurfaceCount:1, artworkMode:"SINGLE" };
    }
    return { physicalFaceCount:1, artworkSurfaceCount:1, artworkMode:"SINGLE" };
  }

  var api = { CONFIG: CONFIG, faceFor: faceFor };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  else root.VVDesignConfig = api;
})(typeof window !== "undefined" ? window : globalThis);
